import { type Configuration, useAdminConfigurationList } from "@sidegigllc/api/client";

export const HIRE_FEE_KEYS = {
  BASE_AMOUNT_CENTS: "HIRE_FEE_BASE_AMOUNT_CENTS",
  DISCOUNT_PER_HOUR_CENTS: "HIRE_FEE_DISCOUNT_PER_HOUR_CENTS",
  MAX_HOURS_THRESHOLD: "HIRE_FEE_MAX_HOURS_THRESHOLD",
} as const;

const REQUIRED_KEYS: ReadonlyArray<string> = [
  HIRE_FEE_KEYS.BASE_AMOUNT_CENTS,
  HIRE_FEE_KEYS.DISCOUNT_PER_HOUR_CENTS,
  HIRE_FEE_KEYS.MAX_HOURS_THRESHOLD,
];

/**
 * Last-resort fallback values used when the global Configuration rows are unreadable or unseeded. They MUST
 * mirror the platform defaults in `api/hire/services/hire_fee_service.py`. The rows come from a staff-only
 * endpoint, so customer-facing screens display the server's `hire_fee` payload and read through this only
 * when no payload was sent; components that *edit* the fee should gate on `isReady`.
 */
export const HIRE_FEE_FALLBACKS: Record<string, number> = {
  [HIRE_FEE_KEYS.BASE_AMOUNT_CENTS]: 150000,
  [HIRE_FEE_KEYS.DISCOUNT_PER_HOUR_CENTS]: 500,
  [HIRE_FEE_KEYS.MAX_HOURS_THRESHOLD]: 300,
};

export type HireFeeScopeSource = "location" | "organization" | "global";

export type ResolvedHireFeeField = {
  cents: number;
  source: HireFeeScopeSource;
  locationRowId?: number;
  organizationRowId?: number;
};

export type HireFeePricing = {
  baseAmount: ResolvedHireFeeField;
  discountPerHour: ResolvedHireFeeField;
  maxHoursThreshold: ResolvedHireFeeField;
  isLoading: boolean;
  // True when the backend has seeded the 3 global Configuration rows. When false, callers
  // should hide hire-fee UI — the backend migration hasn't shipped yet.
  isReady: boolean;
  // Raw scope rows surfaced for callers that need row IDs for upsert/delete (the edit form).
  locationRows: ReadonlyArray<Configuration>;
  organizationRows: ReadonlyArray<Configuration>;
  globalRows: ReadonlyArray<Configuration>;
};

export const findHireFeeRow = (rows: ReadonlyArray<Configuration>, key: string): Configuration | undefined =>
  rows.find((row) => row.key === key);

/**
 * Pure cascade resolver — exposed for unit testing. Walks location -> organization -> global.
 *
 * - Location row present: cents from location, source 'location'. Org row id captured for
 *   downstream callers that may want to also reference the org-scoped row (edit form).
 * - Org row present (no location row): cents from org, source 'organization'.
 * - Else: cents from global row, source 'global'. If no global row at all, cents=0.
 */
export const resolveHireFeeField = (
  key: string,
  locationRows: ReadonlyArray<Configuration>,
  organizationRows: ReadonlyArray<Configuration>,
  globalRow: Configuration | undefined,
): ResolvedHireFeeField => {
  const locationRow = findHireFeeRow(locationRows, key);
  const orgRow = findHireFeeRow(organizationRows, key);
  if (locationRow) {
    return {
      cents: Number.parseInt(locationRow.string_value, 10),
      source: "location",
      locationRowId: locationRow.id,
      organizationRowId: orgRow?.id,
    };
  }
  if (orgRow) {
    return {
      cents: Number.parseInt(orgRow.string_value, 10),
      source: "organization",
      organizationRowId: orgRow.id,
    };
  }
  return {
    // Fall through to the FE fallback when the backend hasn't seeded the global row yet.
    // Mirrors the platform defaults in api/hire/services/hire_fee_service.py.
    cents: globalRow ? Number.parseInt(globalRow.string_value, 10) : (HIRE_FEE_FALLBACKS[key] ?? 0),
    source: "global",
  };
};

export type UseHireFeePricingArgs = {
  locationId?: number;
  organizationId?: string;
  enabled?: boolean;
};

/**
 * Single source of truth for the resolved permanent-hire fee values.
 *
 * Cascades location → organization → global per field independently. When `locationId` is
 * omitted the location-scoped fetch is skipped (org/global only). When `organizationId` is
 * omitted the org-scoped fetch is skipped (global only). Multiple components using the hook
 * with the same args share TanStack Query's cache — one network call per scope per page.
 */
export const useHireFeePricing = ({
  locationId,
  organizationId,
  enabled = true,
}: UseHireFeePricingArgs): HireFeePricing => {
  const hasLocation = enabled && locationId !== undefined;
  const hasOrganization = enabled && organizationId !== undefined;

  const { data: locationData, isLoading: isLoadingLocation } = useAdminConfigurationList(
    { context_type: "location", context_id: hasLocation ? String(locationId) : "", page_size: 100 },
    { query: { enabled: hasLocation } },
  );
  const { data: organizationData, isLoading: isLoadingOrg } = useAdminConfigurationList(
    { context_type: "organization", context_id: organizationId ?? "", page_size: 100 },
    { query: { enabled: hasOrganization } },
  );
  const { data: globalData, isLoading: isLoadingGlobal } = useAdminConfigurationList(
    { context_type: "global", context_id: "0", page_size: 100 },
    { query: { enabled } },
  );

  const locationRows = locationData?.results ?? [];
  const organizationRows = organizationData?.results ?? [];
  const globalRows = globalData?.results ?? [];

  const baseGlobalRow = findHireFeeRow(globalRows, HIRE_FEE_KEYS.BASE_AMOUNT_CENTS);
  const discountGlobalRow = findHireFeeRow(globalRows, HIRE_FEE_KEYS.DISCOUNT_PER_HOUR_CENTS);
  const maxHoursGlobalRow = findHireFeeRow(globalRows, HIRE_FEE_KEYS.MAX_HOURS_THRESHOLD);

  const isReady = REQUIRED_KEYS.every((key) => findHireFeeRow(globalRows, key));
  const isLoading =
    (hasLocation && isLoadingLocation) || (hasOrganization && isLoadingOrg) || (enabled && isLoadingGlobal);

  return {
    baseAmount: resolveHireFeeField(HIRE_FEE_KEYS.BASE_AMOUNT_CENTS, locationRows, organizationRows, baseGlobalRow),
    discountPerHour: resolveHireFeeField(
      HIRE_FEE_KEYS.DISCOUNT_PER_HOUR_CENTS,
      locationRows,
      organizationRows,
      discountGlobalRow,
    ),
    maxHoursThreshold: resolveHireFeeField(
      HIRE_FEE_KEYS.MAX_HOURS_THRESHOLD,
      locationRows,
      organizationRows,
      maxHoursGlobalRow,
    ),
    isLoading,
    isReady,
    locationRows,
    organizationRows,
    globalRows,
  };
};
